import { z } from 'zod/v4';
import { FeatureFlagsSchema } from '../FeatureFlags.js';
import { CacheSettingsSchema, LogCategoriesSchema, LoggingSettingsSchema, LogLevelsSchema, LogRollConfigSchema, ServerSettingsSchema, SystemSettingsSchema, } from '../SystemSettings.js';
import { JellyfinItemFields, JellyfinItemKind } from '../jellyfin/index.js';
import { TruthyQueryParam } from '../schemas/utilSchemas.js';
import { SearchRequestSchema } from '../schemas/SearchRequest.js';
import { ChannelConcatStreamModes, ChannelStreamModes, } from '../schemas/channelSchema.js';
import { CondensedChannelProgramSchema, CondensedContentProgramSchema, ContentProgramSchema, CustomProgramSchema, } from '../schemas/lineups.js';
import { Episode, ItemOrFolder, MusicAlbum, MusicTrack, Season, } from '../schemas/programmingSchema.js';
import { BackupSettingsSchema, EmbyServerSettingsSchema, JellyfinServerSettingsSchema, LocalMediaSourceSchema, PlexServerSettingsSchema, } from '../schemas/settingsSchemas.js';
import { MaterializedSlot, RandomSlotScheduleSchema } from './RandomSlots.js';
import { MaterializedTimeSlot, TimeSlotScheduleSchema } from './TimeSlots.js';
export * from './CommonSlots.js';
export * from './RandomSlots.js';
export * from './Scheduling.js';
export * from './TimeSlots.js';
export * from './plexSearch.js';
export * from './search.js';
export const IdPathParamSchema = z.object({
    id: z.string(),
});
export function PagedResult(schema) {
    return z.object({
        total: z.number(),
        result: schema,
        size: z.number(),
        offset: z.number().optional(),
    });
}
export const ChannelNumberParamSchema = z.object({
    number: z.coerce.number(),
});
export const ChannelLineupQuery = z.object({
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    includePrograms: TruthyQueryParam.default(false),
});
export const LookupExternalProgrammingSchema = z.object({
    externalId: z
        .string()
        .transform((s) => s.split('|', 3)),
});
export const BatchLookupExternalProgrammingSchema = z.object({
    externalIds: z.array(z.string()),
});
export const CreateCustomShowRequestSchema = z.object({
    name: z.string(),
    programs: z.array(CondensedContentProgramSchema).default([]),
    syncMediaSourceId: z.string().nullable(),
    syncMediaSourceType: z.enum(['plex']).nullable(),
    syncExternalPlaylistId: z.string().nullable(),
});
export const UpdateCustomShowRequestSchema = CreateCustomShowRequestSchema.partial().extend({
    enableSync: z.boolean(),
});
export const CreateFillerListRequestSchema = z.object({
    name: z.string(),
    programs: z.array(z.discriminatedUnion('type', [ContentProgramSchema, CustomProgramSchema])),
});
export const UpdateFillerListRequestSchema = CreateFillerListRequestSchema.partial();
export const BasicIdParamSchema = z.object({
    id: z.string(),
});
export const BasicPagingSchema = z.object({
    offset: z.coerce.number().optional(),
    limit: z.coerce.number().optional(),
});
export const ManualProgramLineupSchema = z.object({
    type: z.literal('manual'),
    lineup: CondensedChannelProgramSchema.array(),
    append: z.boolean().default(false),
});
export const TimeBasedProgramLineupSchema = z.object({
    type: z.literal('time'),
    // This must be the list of programs BEFORE any scheduling
    // We do this so that we can potentially create longer schedules
    // on the server-side. However, we can filter this list down to only
    // programs included in at least one time slot...
    programs: z.array(z.string()),
    schedule: TimeSlotScheduleSchema,
    seed: z.number().array().optional(),
    discardCount: z.number().optional(),
});
export const RandomSlotProgramLineupSchema = z.object({
    type: z.literal('random'),
    programs: z.array(z.string()),
    schedule: RandomSlotScheduleSchema,
    seed: z.number().array().optional(),
    discardCount: z.number().optional(),
});
export const UpdateChannelProgrammingRequestSchema = z.discriminatedUnion('type', [
    ManualProgramLineupSchema,
    TimeBasedProgramLineupSchema,
    RandomSlotProgramLineupSchema,
]);
export const UpdateMediaSourceRequestSchema = z.discriminatedUnion('type', [
    PlexServerSettingsSchema.partial({
        sendGuideUpdates: true,
        clientIdentifier: true,
    })
        .omit({ libraries: true })
        .required({
        accessToken: true,
    }),
    JellyfinServerSettingsSchema.omit({ libraries: true }).required({
        accessToken: true,
    }),
    EmbyServerSettingsSchema.omit({ libraries: true }).required({
        accessToken: true,
    }),
    LocalMediaSourceSchema.omit({ libraries: true }),
]);
export const UpdatePlexServerRequestSchema = PlexServerSettingsSchema.partial({
    sendGuideUpdates: true,
    clientIdentifier: true,
    id: true,
});
export const InsertMediaSourceRequestSchema = z.discriminatedUnion('type', [
    PlexServerSettingsSchema.partial({
        sendGuideUpdates: true,
        index: true,
        clientIdentifier: true,
    })
        .omit({ id: true, libraries: true })
        .required({ accessToken: true }),
    JellyfinServerSettingsSchema.omit({ id: true, libraries: true }).required({
        accessToken: true,
    }),
    EmbyServerSettingsSchema.omit({ id: true, libraries: true }).required({
        accessToken: true,
    }),
    LocalMediaSourceSchema.omit({ id: true, libraries: true }),
]);
export const VersionApiResponseSchema = z.object({
    tunarr: z.string(),
    ffmpeg: z.string(),
    nodejs: z.string(),
});
export const BaseErrorSchema = z.object({
    message: z.string(),
});
const FfmpegCoderDetails = z.object({
    name: z.string(),
    ffmpegName: z.string(),
});
export const FfmpegInfoResponse = z.object({
    audioEncoders: z.array(FfmpegCoderDetails),
    videoEncoders: z.array(FfmpegCoderDetails),
    hardwareAccelerationTypes: z.array(z.string()),
});
export const SystemSettingsResponseSchema = SystemSettingsSchema.extend({
    dataDirectory: z.string(),
    logging: LoggingSettingsSchema.extend({
        environmentLogLevel: LogLevelsSchema.optional(),
    }),
    searchServerAddress: z.url(),
});
/**
 * Declared field by field rather than as `LoggingSettingsSchema.pick(...)
 * .partial()`. `.partial()` wraps a field's `.default()` instead of removing
 * it, so a key the client omitted still arrived populated with its default —
 * `useEnvVarLevel` came through as `true` on every request, and
 * `logRollConfig` as the default object. The handler could not distinguish
 * "omitted" from "sent" and reset settings the request never mentioned.
 *
 * `categoryLogLevel` values are nullish: an explicit null clears one category,
 * while a category that is absent is left alone.
 */
export const UpdateSystemSettingsRequestSchema = z.object({
    logging: z
        .object({
        logLevel: LogLevelsSchema.optional(),
        useEnvVarLevel: z.boolean().optional(),
        logRollConfig: LogRollConfigSchema.optional(),
        categoryLogLevel: z
            .partialRecord(LogCategoriesSchema, LogLevelsSchema.nullish())
            .optional(),
    })
        .optional(),
    backup: BackupSettingsSchema.optional(),
    cache: CacheSettingsSchema.optional(),
    server: ServerSettingsSchema.optional(),
});
export const UpdateBackupSettingsRequestSchema = BackupSettingsSchema;
export const JellyfinLoginRequest = z.object({
    url: z.string().url(),
    username: z.string().min(1),
    password: z.string().min(1),
});
export const EmbyLoginRequest = z.object({
    url: z.string().url(),
    username: z.string().min(1),
    password: z.string().min(1),
});
export const StreamConnectionDetailsSchema = z.object({
    ip: z.ipv4().or(z.ipv6()),
    userAgent: z.string().optional(),
    lastHeartbeat: z.number().nonnegative().optional(),
});
export const ChannelSessionsResponseSchema = z.object({
    // TODO: Share types with session
    type: z.enum([...ChannelStreamModes, ...ChannelConcatStreamModes]),
    state: z.string(),
    numConnections: z.number().nonnegative(),
    connections: z.array(StreamConnectionDetailsSchema),
});
export const JellyfinGetLibraryItemsQuerySchema = z.object({
    offset: z.coerce.number().nonnegative().optional(),
    limit: z.coerce.number().positive().optional(),
    itemTypes: z
        .string()
        .optional()
        .transform((s) => s?.split(','))
        .pipe(JellyfinItemKind.array().optional()),
    extraFields: z
        .string()
        .optional()
        .transform((s) => s?.split(','))
        .pipe(JellyfinItemFields.array().optional()),
    // pipe delimited
    genres: z
        .string()
        .optional()
        .transform((s) => s?.split('|').filter((s) => s.trim().length > 0)),
    nameStartsWithOrGreater: z.string().min(1).optional(),
    nameStartsWith: z.string().min(1).optional(),
    nameLessThan: z.string().min(1).optional(),
});
export const MediaSourceHealthyStatusSchema = z.object({
    healthy: z.literal(true),
});
export const MediaSourceUnhealthyStatusSchema = z.object({
    healthy: z.literal(false),
    status: z.enum(['unreachable', 'auth', 'timeout', 'bad_response', 'unknown']),
});
export const MediaSourceStatusSchema = MediaSourceHealthyStatusSchema.or(MediaSourceUnhealthyStatusSchema);
export const TimeSlotScheduleResult = z.object({
    startTime: z.number().positive(),
    lineup: CondensedChannelProgramSchema.array(),
    // programs: z.record(z.string(), ContentProgramSchema),
    seed: z.number().array(),
    discardCount: z.number(),
});
export const TimeSlotScheduleWithPrograms = z.object({
    ...TimeSlotScheduleResult.shape,
    programs: z.record(z.string(), ContentProgramSchema),
});
export const SlotScheduleResult = z.object({
    startTime: z.number().positive(),
    lineup: CondensedChannelProgramSchema.array(),
    seed: z.number().array(),
    discardCount: z.number(),
});
export const SlotScheduleWithPrograms = z.object({
    ...SlotScheduleResult.shape,
    programs: z.record(z.string(), ContentProgramSchema),
});
export const ProgramChildrenResult = PagedResult(z.discriminatedUnion('type', [
    z.object({
        type: z.literal('season'),
        programs: Season.array(),
    }),
    z.object({
        type: z.literal('album'),
        programs: MusicAlbum.array(),
    }),
    z.object({
        type: z.literal('episode'),
        programs: Episode.array(),
    }),
    z.object({
        type: z.literal('track'),
        programs: MusicTrack.array(),
    }),
]));
export const UpdateMediaSourceLibraryRequest = z.object({
    enabled: z.boolean(),
});
export const ProgramSearchRequest = z.object({
    query: SearchRequestSchema,
    restrictSeachTo: z.array(z.string()).optional(),
    mediaSourceId: z.string().optional(),
    libraryId: z.string().optional(), // Limit search to a specific library
    page: z.number().optional(),
    limit: z.number().optional(),
    expandParents: z.boolean().optional(),
});
export const ProgramSearchResponse = z.object({
    results: z.array(ItemOrFolder),
    page: z.number(),
    totalPages: z.number(),
    totalHits: z.number(),
    facetDistribution: z
        .record(z.string(), z.record(z.string(), z.number()))
        .optional(),
});
const NotScanningSchema = z.object({
    state: z.literal('not_scanning'),
});
const InProgressScanSchema = z.object({
    state: z.literal('in_progress'),
    startedAt: z.number(), // timestamp
    percentComplete: z.number().min(0).max(100),
});
const QueuedProgressScanSchema = z.object({
    state: z.literal('queued'),
});
export const ScanProgressSchema = z.discriminatedUnion('state', [
    InProgressScanSchema,
    NotScanningSchema,
    QueuedProgressScanSchema,
]);
export const MaterializedTimeSlotSchedule = z.object({
    ...TimeSlotScheduleSchema.shape,
    slots: MaterializedTimeSlot.array(),
});
export const MaterializedSlotSchedule = z.object({
    ...RandomSlotScheduleSchema.shape,
    slots: MaterializedSlot.array(),
});
export const MaterializedSchedule = z.discriminatedUnion('type', [
    MaterializedTimeSlotSchedule,
    MaterializedSlotSchedule,
]);
export const FeatureFlagResponseMetaSchema = z.object({
    key: z.string(),
    displayName: z.string(),
    description: z.string(),
    category: z.enum(['experimental', 'escape-hatch']),
    envOverride: z.boolean(),
});
export const GetFeatureFlagsResponseSchema = z.object({
    flags: FeatureFlagsSchema,
    metadata: z.array(FeatureFlagResponseMetaSchema),
});
export const UpdateFeatureFlagsRequestSchema = FeatureFlagsSchema.partial();
//# sourceMappingURL=index.js.map